import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Download, FileSpreadsheet, Loader2, RefreshCw, List, BarChart3 } from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';

const ScheduleEPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  // Summary data
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  
  // Detail data
  const [bases, setBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [baseDetail, setBaseDetail] = useState(null);
  const [basesLoading, setBasesLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  
  // Run selection
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  
  // View mode
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'detail'

  // Load available runs on mount
  useEffect(() => {
    const loadRuns = async () => {
      if (!clientId) return;
      try {
        const res = await fetch(`${API}/gl-calcs/runs/recent?limit=10&tenant_id=${clientId}`);
        if (res.ok) {
          const data = await res.json();
          setRuns(data.runs || []);
          if (data.runs && data.runs.length > 0) {
            setSelectedRunId(data.runs[0].run_id);
          }
        }
      } catch (err) {
        console.error('Failed to load runs:', err);
      }
    };
    loadRuns();
  }, [clientId]);

  // Load summary when run changes
  useEffect(() => {
    const loadSummary = async () => {
      if (!selectedRunId) return;
      
      try {
        setSummaryLoading(true);
        const res = await fetch(`${API}/gl-calcs/schedule-e/${selectedRunId}`);
        if (res.ok) {
          const data = await res.json();
          setSummaryData(data.section);
        }
      } catch (err) {
        console.error('Failed to load Schedule E summary:', err);
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, [selectedRunId]);

  // Load bases list when run changes
  useEffect(() => {
    const loadBases = async () => {
      if (!selectedRunId) return;
      
      try {
        setBasesLoading(true);
        setSelectedBase(null);
        setDetailError(null);
        setBases([]);
        setBaseDetail(null);
        const res = await fetch(`${API}/gl-calcs/schedule-e/detail/${selectedRunId}?include_totals=false`);
        if (res.ok) {
          const data = await res.json();
          const nextBases = data.bases || [];
          setBases(nextBases);
          if (nextBases.length > 0) {
            setSelectedBase(prev => (
              prev && nextBases.some(base => base.key === prev) ? prev : nextBases[0].key
            ));
          } else {
            setSelectedBase(null);
          }
        } else {
          const msg = await res.text();
          setDetailError(`Failed to load base list (${res.status})${msg ? `: ${msg}` : ''}`);
        }
      } catch (err) {
        console.error('Failed to load bases:', err);
        setDetailError(`Failed to load base list: ${err.message}`);
      } finally {
        setBasesLoading(false);
      }
    };
    loadBases();
  }, [selectedRunId]);

  // Load base detail when selection changes
  useEffect(() => {
    const loadBaseDetail = async () => {
      if (!selectedRunId || !selectedBase) return;
      
      try {
        setDetailLoading(true);
        setDetailError(null);
        const res = await fetch(`${API}/gl-calcs/schedule-e/detail/${selectedRunId}?base_key=${encodeURIComponent(selectedBase)}`);
        if (res.ok) {
          const data = await res.json();
          setBaseDetail(data);
        } else {
          const msg = await res.text();
          setBaseDetail(null);
          setDetailError(`Failed to load account details (${res.status})${msg ? `: ${msg}` : ''}`);
        }
      } catch (err) {
        console.error('Failed to load base detail:', err);
        setBaseDetail(null);
        setDetailError(`Failed to load account details: ${err.message}`);
      } finally {
        setDetailLoading(false);
      }
    };
    loadBaseDetail();
  }, [selectedRunId, selectedBase]);

  const formatRunLabel = (run) => {
    const rawDate = run?.created_at || run?.timestamp || run?.calculated_at || null;
    const parsed = rawDate ? new Date(rawDate) : null;
    const dateLabel = parsed && !Number.isNaN(parsed.getTime())
      ? parsed.toLocaleDateString()
      : 'Unknown Date';
    const runId = run?.run_id || 'unknown';
    return `${dateLabel} - ${runId.substring(0, 12)}...`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatRate = (value) => {
    if (value === null || value === undefined || value === 0) return '—';
    return `${(Number(value) * 100).toFixed(4)}%`;
  };

  // Group bases by category
  const fringeBases = useMemo(() => bases.filter(b => b.key.startsWith('fringe_')), [bases]);
  const divisionBases = useMemo(() => bases.filter(b => b.key.startsWith('oh_')), [bases]);
  const gaBases = useMemo(() => bases.filter(b => b.key.startsWith('ga_') || b.key === 'smh_base'), [bases]);

  const renderSummaryView = () => {
    if (summaryLoading) {
      return (
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2">Loading Schedule E summary...</span>
        </div>
      );
    }

    if (!summaryData) {
      return (
        <div className="py-12 text-center text-gray-500">
          No Schedule E data available. Run GL Calculations first.
        </div>
      );
    }

    const summary = summaryData.summary || {};
    const fringeBases = summaryData.fringe_bases || {};
    const divisionOhBases = summaryData.division_oh_bases || {};
    const materialHandlingBase = summaryData.material_handling_base || {};
    const gaComponents = summaryData.ga_base_components || {};

    // Phase 7: derive a per-fringe rate card per row in
    // ``fringeBases.rows`` instead of the hardcoded "Fringe 1 Rate" /
    // "Fringe 2 Rate" slots reading positional ``summary.fringe_100_rate``
    // / ``summary.fringe_101_rate``. Wizard clients with arbitrary
    // fringe section IDs and any number of fringe pools get one card
    // per pool; legacy reference-client runs still get two cards
    // labeled "Fringe 1 Rate" / "Fringe 2 Rate" via the executor's
    // ``_LEGACY_LABEL_FALLBACKS`` fallback table.
    const fringeRateCards = (fringeBases.rows || []).map((row, idx) => {
      const baseLabel = (row.label || `Fringe ${idx + 1}`)
        .replace(/\s+Base\s*$/i, '')
        .trim() || `Fringe ${idx + 1}`;
      return {
        key: row.key || `fringe_${idx}`,
        title: `${baseLabel} Rate`,
        rate: row.rate,
      };
    });

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {fringeRateCards.map((card) => (
            <Card key={card.key}>
              <CardContent className="pt-4">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{card.title}</div>
                <div className="text-2xl font-bold text-green-600">{formatRate(card.rate)}</div>
              </CardContent>
            </Card>
          ))}
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Division Bases</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_division_bases)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">G&A Rate</div>
              <div className="text-2xl font-bold text-orange-600">{formatRate(summary.ga_rate)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Fringe Bases */}
        <Card>
          <CardHeader className="py-3" style={{ background: '#E2EFDA' }}>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">Fringe Benefit Bases</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Description</th>
                  <th className="px-4 py-3 text-right font-semibold">Base</th>
                  <th className="px-4 py-3 text-right font-semibold">Pool</th>
                  <th className="px-4 py-3 text-right font-semibold">Rate</th>
                </tr>
              </thead>
              <tbody>
                {(fringeBases.rows || []).map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 font-medium">{row.label}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.value)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.pool)}</td>
                    <td className="px-4 py-2 text-right font-mono text-green-600">{formatRate(row.rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Division OH Bases */}
        <Card>
          <CardHeader className="py-3" style={{ background: '#DDEBF7' }}>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">Division OH Bases</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Division</th>
                  <th className="px-4 py-3 text-right font-semibold">Labor Base</th>
                  <th className="px-4 py-3 text-right font-semibold">Fringe Amount</th>
                  {/* Phase 7: only render the secondary fringe column
                      when at least one row carries a non-null
                      ``fringe_101_amount``. Wizard clients with a
                      single fringe pool drop the column entirely
                      instead of showing a column of dashes. */}
                  {(divisionOhBases.rows || []).some(r => r && r.fringe_101_amount != null) && (
                    <th className="px-4 py-3 text-right font-semibold">Fringe 2</th>
                  )}
                  <th className="px-4 py-3 text-right font-semibold">Total Base</th>
                  <th className="px-4 py-3 text-right font-semibold">OH Rate</th>
                  <th className="px-4 py-3 text-right font-semibold">Total Pool</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const hasSecondaryFringe = (divisionOhBases.rows || []).some(r => r && r.fringe_101_amount != null);
                  return (divisionOhBases.rows || []).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 font-medium">{row.label}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.labor_base)}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.fringe_amount)}</td>
                      {hasSecondaryFringe && (
                        <td className="px-4 py-2 text-right font-mono">{row.fringe_101_amount != null ? formatCurrency(row.fringe_101_amount) : '—'}</td>
                      )}
                      <td className="px-4 py-2 text-right font-mono font-semibold">{formatCurrency(row.total_base)}</td>
                      <td className="px-4 py-2 text-right font-mono text-green-600">{formatRate(row.oh_rate)}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.total_pool)}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Material & Handling Base */}
        {(materialHandlingBase.rows || []).length > 0 && (
          <Card>
            <CardHeader className="py-3" style={{ background: '#FFF2CC' }}>
              <CardTitle className="text-sm font-semibold uppercase tracking-wide">Material & Handling Base</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-4 py-3 text-left font-semibold">Description</th>
                    <th className="px-4 py-3 text-right font-semibold">Base</th>
                    <th className="px-4 py-3 text-right font-semibold">Pool</th>
                    <th className="px-4 py-3 text-right font-semibold">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(materialHandlingBase.rows || []).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 font-medium">{row.label}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.value)}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.pool)}</td>
                      <td className="px-4 py-2 text-right font-mono text-green-600">{formatRate(row.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* G&A Base Components */}
        <Card>
          <CardHeader className="py-3" style={{ background: '#FCE4D6' }}>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">G&A Base Components</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Component</th>
                  <th className="px-4 py-3 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white"><td className="px-4 py-2">Direct Labor (Account 500)</td><td className="px-4 py-2 text-right font-mono">{formatCurrency(gaComponents.direct_labor)}</td></tr>
                <tr className="bg-gray-50"><td className="px-4 py-2">Fringe on Direct Labor</td><td className="px-4 py-2 text-right font-mono">{formatCurrency(gaComponents.fringe_on_labor)}</td></tr>
                {!!gaComponents.division_oh_total && <tr className="bg-white"><td className="px-4 py-2">Division OH Total</td><td className="px-4 py-2 text-right font-mono">{formatCurrency(gaComponents.division_oh_total)}</td></tr>}
                {!!gaComponents.travel_510 && <tr className="bg-gray-50"><td className="px-4 py-2">Travel (Account 510)</td><td className="px-4 py-2 text-right font-mono">{formatCurrency(gaComponents.travel_510)}</td></tr>}
                {!!gaComponents.odc_520 && <tr className="bg-white"><td className="px-4 py-2">Other Direct Costs (Account 520)</td><td className="px-4 py-2 text-right font-mono">{formatCurrency(gaComponents.odc_520)}</td></tr>}
                {!!gaComponents.material_handling && <tr className="bg-gray-50"><td className="px-4 py-2">Material & Handling</td><td className="px-4 py-2 text-right font-mono">{formatCurrency(gaComponents.material_handling)}</td></tr>}
                <tr className="bg-amber-50 font-bold border-t-2 border-gray-300">
                  <td className="px-4 py-3">G&A Total Base</td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(gaComponents.total_base)}</td>
                </tr>
                <tr className="bg-green-50 font-semibold">
                  <td className="px-4 py-3">G&A Rate</td>
                  <td className="px-4 py-3 text-right font-mono text-green-600">{formatRate(gaComponents.rate)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Division OH Breakdown for G&A */}
        {gaComponents.division_oh_breakdown && gaComponents.division_oh_breakdown.length > 0 && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide">Division OH Breakdown (included in G&A Base)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-4 py-3 text-left font-semibold">Division</th>
                    <th className="px-4 py-3 text-right font-semibold">OH Allocation</th>
                    <th className="px-4 py-3 text-right font-semibold">Base</th>
                    <th className="px-4 py-3 text-right font-semibold">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {gaComponents.division_oh_breakdown.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 font-medium">{row.label}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.oh_allocation)}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatCurrency(row.base)}</td>
                      <td className="px-4 py-2 text-right font-mono text-green-600">{formatRate(row.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderDetailView = () => {
    if (basesLoading) {
      return (
        <div className="py-10 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2">Loading Schedule E bases...</span>
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="py-8 text-center text-red-600">
          {detailError}
        </div>
      );
    }

    if (!bases.length) {
      return (
        <div className="py-8 text-center text-gray-500">
          No Schedule E base data found for this run.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Category Labels with Tabs */}
        <div className="space-y-4">
          {/* Fringe Bases */}
          {fringeBases.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-3 py-2 rounded" style={{ background: '#E2EFDA' }}>
                Fringe Benefit Bases
              </div>
              <div className="flex flex-wrap gap-2">
                {fringeBases.map(base => (
                  <Button
                    key={base.key}
                    variant={selectedBase === base.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedBase(base.key)}
                    className="text-xs"
                  >
                    {base.label.replace(' Base', '')}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Division OH Bases */}
          {divisionBases.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-3 py-2 rounded" style={{ background: '#DDEBF7' }}>
                Division Overhead Bases
              </div>
              <div className="flex flex-wrap gap-2">
                {divisionBases.map(base => (
                  <Button
                    key={base.key}
                    variant={selectedBase === base.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedBase(base.key)}
                    className="text-xs"
                  >
                    {base.label.replace(' OH Base', '').replace('Division ', 'Div ')}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* G&A Bases */}
          {gaBases.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-3 py-2 rounded" style={{ background: '#FCE4D6' }}>
                G&A Base Components
              </div>
              <div className="flex flex-wrap gap-2">
                {gaBases.map(base => (
                  <Button
                    key={base.key}
                    variant={selectedBase === base.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedBase(base.key)}
                    className="text-xs"
                  >
                    {base.label.replace('G&A Base - ', '').replace(' Base', '')}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Account Detail */}
        {detailLoading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2">Loading accounts...</span>
          </div>
        ) : baseDetail ? (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{baseDetail.label}</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">{baseDetail.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase">Reference</div>
                  <div className="text-sm font-medium">{baseDetail.ref}</div>
                </div>
              </div>
              <div className="flex gap-6 mt-3">
                <div>
                  <span className="text-xs text-gray-500 uppercase">Total</span>
                  <div className="text-xl font-bold text-blue-600">{formatCurrency(baseDetail.total)}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase">Accounts</span>
                  <div className="text-xl font-bold text-gray-700">{baseDetail.account_count}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-4 py-3 text-left font-semibold">Lookup Code</th>
                      <th className="px-4 py-3 text-left font-semibold">Pool Code</th>
                      <th className="px-4 py-3 text-left font-semibold">Account</th>
                      <th className="px-4 py-3 text-right font-semibold">Expenses per G/L</th>
                      <th className="px-4 py-3 text-right font-semibold">Expenses Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baseDetail.accounts && baseDetail.accounts.map((acc, idx) => {
                      // Handle section header rows
                      if (acc.is_header) {
                        return (
                          <tr key={idx} className="bg-blue-100">
                            <td colSpan={5} className="px-4 py-3 font-bold text-blue-900">
                              {acc.section_label}
                            </td>
                          </tr>
                        );
                      }
                      
                      // Handle subtotal rows
                      if (acc.is_subtotal) {
                        return (
                          <tr key={idx} className="bg-green-50 font-semibold border-t border-gray-200">
                            <td className="px-4 py-2" colSpan={3}></td>
                            <td className="px-4 py-2 text-right font-mono text-green-700">{acc.account}</td>
                            <td className="px-4 py-2 text-right font-mono text-green-700">{formatCurrency(acc.expenses_claimed)}</td>
                          </tr>
                        );
                      }
                      
                      // Handle fringe rows (light blue background)
                      if (acc.is_fringe) {
                        return (
                          <tr key={idx} className="bg-cyan-50 font-semibold italic border-t border-gray-200">
                            <td className="px-4 py-2" colSpan={3}></td>
                            <td className="px-4 py-2 text-right font-mono text-cyan-700">{acc.account}</td>
                            <td className="px-4 py-2 text-right font-mono text-cyan-700">{formatCurrency(acc.expenses_claimed)}</td>
                          </tr>
                        );
                      }
                      
                      // Handle grand total row
                      if (acc.is_total) {
                        return (
                          <tr key={idx} className="bg-amber-100 font-bold border-t-2 border-gray-400">
                            <td className="px-4 py-3" colSpan={3}></td>
                            <td className="px-4 py-3 text-right font-mono">{acc.account}</td>
                            <td className="px-4 py-3 text-right font-mono">{formatCurrency(acc.expenses_claimed)}</td>
                          </tr>
                        );
                      }
                      
                      // Regular account rows
                      return (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-mono text-xs">{acc.lookup_code}</td>
                          <td className="px-4 py-2">{acc.pool_code}</td>
                          <td className="px-4 py-2 font-mono text-xs">{acc.account}</td>
                          <td className="px-4 py-2 text-right font-mono">{formatCurrency(acc.expenses_per_gl)}</td>
                          <td className="px-4 py-2 text-right font-mono">{formatCurrency(acc.expenses_claimed)}</td>
                        </tr>
                      );
                    })}
                    {/* Show total row only for non-combined bases */}
                    {baseDetail.accounts && !baseDetail.accounts.some(acc => acc.is_total) && (
                      <tr className="bg-amber-50 font-bold border-t-2 border-gray-300">
                        <td className="px-4 py-3" colSpan={3}>Total {baseDetail.label}</td>
                        <td className="px-4 py-3 text-right font-mono">{formatCurrency(baseDetail.total)}</td>
                        <td className="px-4 py-3 text-right font-mono">{formatCurrency(baseDetail.total)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="py-8 text-center text-gray-500">
            Select a base to view account details
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule E - Bases</h1>
          <p className="text-sm text-gray-600 mt-1">Allocation bases used for indirect cost rate calculations</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Run Selector */}
          <select
            value={selectedRunId || ''}
            onChange={(e) => setSelectedRunId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {runs.length === 0 && (
              <option value="">No runs available</option>
            )}
            {runs.map(run => (
              <option key={run.run_id} value={run.run_id}>
                {formatRunLabel(run)}
              </option>
            ))}
          </select>

          {/* View Mode Toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setViewMode('summary')}
              className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                viewMode === 'summary' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              Summary
            </button>
            <button
              onClick={() => setViewMode('detail')}
              className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                viewMode === 'detail' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="h-4 w-4" />
              Account Details
            </button>
          </div>

          {/* Export Buttons */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`${API}/gl-calcs/export/schedule-e/${selectedRunId}`, '_blank')}
            disabled={!selectedRunId}
          >
            <Download className="h-4 w-4 mr-1" />
            Export Summary
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => window.open(`${API}/gl-calcs/export/schedule-e-detail/${selectedRunId}`, '_blank')}
            disabled={!selectedRunId}
            className="bg-green-600 hover:bg-green-700"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1" />
            Export All Tabs
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'summary' ? renderSummaryView() : renderDetailView()}
    </div>
  );
};

export default ScheduleEPage;
